import {
  Component,
  effect,
  EventEmitter,
  inject,
  model,
  ModelSignal,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  CSV_EXPORT_ACTION,
  DUPLICATE_ACTION,
  ITableAction, NEW_ENTRY_ACTION,
  SharedGridOptions,
  TableControlComponent,
  TableControlModule,
} from 'frontier/browserkit';
import {EControlActions, EsvgFiles} from 'frontier/nucleus';
import {GridOptions, ModelUpdatedEvent} from 'ag-grid-community';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Observable, of} from 'rxjs';
import {finalize, tap} from 'rxjs/operators';
import {DunningLevelViewService, TDBObject} from 'src/app/api/generated';
import AppControlGuids from '../../../../api/ControlGuids';

@Component({
  selector: 'app-dunning-config-table',
  templateUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.html',
  styleUrl: '../../../../../../projects/frontier/browserkit/src/lib/components/control/table-control/table-control.component.scss',
  standalone: true,
  imports: [TableControlModule],
  host: {
    '[style.height.px]': 'tableHeight()',
  },
})
export class DunningConfigTableComponent extends TableControlComponent {
  private _snackbar: MatSnackBar = inject(MatSnackBar);
  private _dunningLevelView: DunningLevelViewService = inject(DunningLevelViewService);

  editedRow: WritableSignal<TDBObject> = signal<TDBObject>(null);
  tableHeight: WritableSignal<number> = signal<number>(150);

  @Output() editedRowChange : EventEmitter<TDBObject> = new EventEmitter<TDBObject>();

  override GUID: string = AppControlGuids.TDunningConfigView;
  override gridOptions: GridOptions = {
    ...SharedGridOptions,
    rowSelection: 'single',
    onModelUpdated: (event: ModelUpdatedEvent): void => {
      const rowCount: number = event.api.getDisplayedRowCount() || 2; // Space for noRows display
      const rowHeight: number = this.gridOptions.rowHeight || 30;
      const headerHeight: number = this.gridOptions.headerHeight || 35;
      const actionBarHeight: number = this.actions().length > 0 ? 46 : 0;
      const calculatedHeight: number = headerHeight + actionBarHeight + (rowCount * rowHeight);
      this.tableHeight.set(calculatedHeight);
    },
  };
  override actions: ModelSignal<ITableAction[]> = model<ITableAction[]>([
    NEW_ENTRY_ACTION,
    {
      displayName: 'Löschen',
      controlAction: EControlActions.delete,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: false,
      color: 'warn',
      icon: EsvgFiles.delete,
      isDisabled: (): boolean => {
        return !!this.editedRow();
      },
    },
    CSV_EXPORT_ACTION,
    DUPLICATE_ACTION,
    {
      displayName: 'Mahnprüflauf durchführen',
      controlAction: EControlActions.dunning,
      disabledIfNoSelection: false,
      disabledIfMultiSelection: false,
      color: 'accent',
      icon: EsvgFiles.search,
      action: (): Observable<boolean> => this.startDunningCheck(),
    },
    {
      displayName: (): string => this.editedRow() ? 'Bearbeitung beenden' : 'Mahnungsvorlagen bearbeiten',
      controlAction: EControlActions.edit,
      disabledIfNoSelection: true,
      disabledIfMultiSelection: true,
      color: 'primary',
      icon: EsvgFiles.edit,
      tooltip: 'Blende die Mahnungsvorlagen ein.',
      tooltipDelay: 1000,
      action: (): Observable<null> => this.toggleEditing(),
    },
  ]);

  constructor() {
    super();

    effect((): void => {
      const editedRow: TDBObject = this.editedRow();
      this.patchFilter({parent: editedRow});
      this.editedRowChange.emit(editedRow);
    }, {allowSignalWrites: true});
  }

  private startDunningCheck(): Observable<boolean> {
    return this._dunningLevelView.dunningLevelViewStartDunningCheck().pipe(
      tap((res: boolean): void => {
        if (res) {
          this._snackbar.open('Juhu, Mahnprüflauf erfolgreich durchgeführt.', 'ok', {
            verticalPosition: 'top',
            duration: 3000,
          });
        }
      }),
      finalize((): void => {
        this.gridApi?.hideOverlay();
      }),
    );
  }

  private toggleEditing(): Observable<null> {
    if (this.editedRow()) {
      this.editedRow.set(null);
    } else {
      this.editedRow.set(this.selectedRows()[0].apiRow.obj);
    }
    return of(null);
  }
}
